import * as Sentry from '@sentry/react'

const PRELOAD_ERROR_EVENT = 'vite:preloadError'
const LEGACY_RECOVERED_APP_VERSION_KEY = 'driver360:preload-recovery-version'

export const RECOVERY_ATTEMPTS_KEY = 'driver360:preload-recovery-attempts'

export const MAX_AUTOMATIC_RELOADS = 2
export const RECOVERY_WINDOW_MS = 5 * 60 * 1000

const MAX_URL_LENGTH = 512
const MAX_DIAGNOSTICS_PER_DOCUMENT = 10
const ASSET_URL_PATTERN = /(?:https?:\/\/|\/)[^\s'"()]+/

type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

interface RecoveryAttempt {
  entryVersion: string
  attemptedAt: number
}

export type PreloadRecoveryDecision = 'attempted' | 'skipped' | 'unavailable'

export type PreloadRecoveryReason =
  | 'automatic_reload'
  | 'version_already_attempted'
  | 'reload_limit_reached'
  | 'storage_unavailable'
  | 'reload_failed'

export interface PreloadRecoveryDiagnostic {
  decision: PreloadRecoveryDecision
  reason: PreloadRecoveryReason
  entryVersion: string
  assetUrl?: string
  attemptCount: number
}

interface PreloadErrorRecoveryOptions {
  target?: EventTarget
  getStorage?: () => RecoveryStorage
  getAppVersion?: () => string
  reload?: () => void
  now?: () => number
  report?: (diagnostic: PreloadRecoveryDiagnostic) => void
}

let isDocumentReloadPending = false

export const isPreloadReloadPending = () => isDocumentReloadPending

const getCurrentAppVersion = () =>
  document.querySelector<HTMLScriptElement>('script[type="module"][src]')
    ?.src ?? 'unknown'

export const sanitizeUrl = (value: string) => {
  try {
    const url = new URL(value, window.location.href)
    return `${url.origin}${url.pathname}`.slice(0, MAX_URL_LENGTH)
  } catch {
    return value.split(/[?#]/)[0].slice(0, MAX_URL_LENGTH)
  }
}

const getFailedAssetUrl = (event: Event) => {
  const { payload } = event as Event & { payload?: unknown }
  const message = payload instanceof Error ? payload.message : ''
  const match = message.match(ASSET_URL_PATTERN)

  return match ? sanitizeUrl(match[0]) : undefined
}

const isRecoveryAttempt = (value: unknown): value is RecoveryAttempt => {
  if (typeof value !== 'object' || value === null) return false

  const { entryVersion, attemptedAt } = value as Partial<RecoveryAttempt>

  return (
    typeof entryVersion === 'string' &&
    entryVersion.length > 0 &&
    entryVersion.length <= MAX_URL_LENGTH &&
    typeof attemptedAt === 'number' &&
    Number.isFinite(attemptedAt)
  )
}

const parseAttempts = (raw: string | null): RecoveryAttempt[] => {
  if (!raw) return []

  try {
    const parsed: unknown = JSON.parse(raw)
    const attempts = (parsed as { attempts?: unknown })?.attempts

    return Array.isArray(attempts) ? attempts.filter(isRecoveryAttempt) : []
  } catch {
    return []
  }
}

const serializeAttempts = (attempts: RecoveryAttempt[]) =>
  JSON.stringify({ attempts })

const writeAttempts = (
  storage: RecoveryStorage,
  attempts: RecoveryAttempt[],
) => {
  const serialized = serializeAttempts(attempts)
  storage.setItem(RECOVERY_ATTEMPTS_KEY, serialized)

  if (storage.getItem(RECOVERY_ATTEMPTS_KEY) !== serialized) {
    throw new Error('Preload recovery guard was not persisted')
  }
}

const readAttempts = (storage: RecoveryStorage, now: number) => {
  const raw = storage.getItem(RECOVERY_ATTEMPTS_KEY)
  const legacyVersion = storage.getItem(LEGACY_RECOVERED_APP_VERSION_KEY)
  const attempts = parseAttempts(raw)

  if (legacyVersion) {
    attempts.push({
      entryVersion: sanitizeUrl(legacyVersion),
      attemptedAt: now,
    })
  }

  const active = attempts
    .map((attempt) => ({
      ...attempt,
      attemptedAt: Math.min(attempt.attemptedAt, now),
    }))
    .filter((attempt) => now - attempt.attemptedAt < RECOVERY_WINDOW_MS)
    .sort((a, b) => a.attemptedAt - b.attemptedAt)
    .slice(-MAX_AUTOMATIC_RELOADS)

  if (serializeAttempts(active) !== raw) {
    writeAttempts(storage, active)
  }

  if (legacyVersion !== null) {
    storage.removeItem(LEGACY_RECOVERED_APP_VERSION_KEY)
  }

  return active
}

const reportToSentry = (diagnostic: PreloadRecoveryDiagnostic) => {
  Sentry.captureMessage('Lazy chunk preload recovery', {
    level: diagnostic.decision === 'attempted' ? 'info' : 'warning',
    fingerprint: ['preload-recovery', diagnostic.decision, diagnostic.reason],
    tags: {
      preload_recovery_decision: diagnostic.decision,
      preload_recovery_reason: diagnostic.reason,
    },
    extra: {
      entryVersion: diagnostic.entryVersion,
      assetUrl: diagnostic.assetUrl,
      attemptCount: diagnostic.attemptCount,
    },
  })
}

export const registerPreloadErrorRecovery = (
  options: PreloadErrorRecoveryOptions = {},
) => {
  const {
    target = window,
    getStorage = () => window.sessionStorage,
    getAppVersion = getCurrentAppVersion,
    reload = () => window.location.reload(),
    now = Date.now,
    report = reportToSentry,
  } = options

  const reportedDiagnostics = new Set<string>()
  let isReloadPending = false

  const emit = (diagnostic: PreloadRecoveryDiagnostic) => {
    const key = [
      diagnostic.decision,
      diagnostic.reason,
      diagnostic.entryVersion,
      diagnostic.assetUrl,
    ].join('|')

    if (
      reportedDiagnostics.has(key) ||
      reportedDiagnostics.size >= MAX_DIAGNOSTICS_PER_DOCUMENT
    ) {
      return
    }

    reportedDiagnostics.add(key)

    try {
      report(diagnostic)
    } catch {
      // Telemetry must never block recovery.
    }
  }

  const handlePreloadError: EventListener = (event) => {
    if (isReloadPending) {
      event.preventDefault()
      return
    }

    const entryVersion = sanitizeUrl(getAppVersion())
    const assetUrl = getFailedAssetUrl(event)
    const timestamp = now()
    const decide = (
      decision: PreloadRecoveryDecision,
      reason: PreloadRecoveryReason,
      attemptCount: number,
    ) => emit({ decision, reason, entryVersion, assetUrl, attemptCount })

    let attempts: RecoveryAttempt[]

    try {
      const storage = getStorage()
      attempts = readAttempts(storage, timestamp)

      if (attempts.some((attempt) => attempt.entryVersion === entryVersion)) {
        decide('skipped', 'version_already_attempted', attempts.length)
        return
      }

      if (attempts.length >= MAX_AUTOMATIC_RELOADS) {
        decide('skipped', 'reload_limit_reached', attempts.length)
        return
      }

      attempts = [...attempts, { entryVersion, attemptedAt: timestamp }]
      writeAttempts(storage, attempts)
    } catch {
      decide('unavailable', 'storage_unavailable', 0)
      return
    }

    decide('attempted', 'automatic_reload', attempts.length)
    isReloadPending = true
    isDocumentReloadPending = true

    try {
      reload()
    } catch {
      isReloadPending = false
      isDocumentReloadPending = false
      decide('unavailable', 'reload_failed', attempts.length)
      return
    }

    event.preventDefault()
  }

  target.addEventListener(PRELOAD_ERROR_EVENT, handlePreloadError)

  return () =>
    target.removeEventListener(PRELOAD_ERROR_EVENT, handlePreloadError)
}
